import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Target, Flame, Trophy, Users, Calendar, CheckCircle, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

interface ChallengesViewProps {
  companyId: string;
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  target_type: string;
  created_at: string;
  expires_at: string | null;
  content: {
    challenge_type?: string;
    duration_days?: number;
    target_days?: number;
  };
  participants: ChallengeParticipant[];
}

interface ChallengeParticipant {
  worker_id: string;
  worker_name: string;
  team: string;
  status: string;
  completed_at: string | null;
  current_streak: number;
  daily_completions: string[];
}

export default function ChallengesView({ companyId }: ChallengesViewProps) {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChallenges();
  }, [companyId]);

  const loadChallenges = async () => {
    try {
      // Get challenge templates
      const { data: templates, error: templatesError } = await supabase
        .from('quest_templates')
        .select('*')
        .eq('company_id', companyId)
        .in('intervention_type', ['challenge', 'cluster_challenge'])
        .order('created_at', { ascending: false });

      if (templatesError) throw templatesError;

      // Get worker quests for each template
      const challengeData: Challenge[] = [];

      for (const template of templates || []) {
        const { data: quests } = await supabase
          .from('worker_quests')
          .select(`
            id,
            worker_id,
            status,
            completed_at,
            response_data,
            worker_profiles!inner(display_name, team)
          `)
          .eq('template_id', template.id);

        const participants: ChallengeParticipant[] = (quests || []).map((q: any) => ({
          worker_id: q.worker_id,
          worker_name: q.worker_profiles?.display_name || 'Unknown',
          team: q.worker_profiles?.team || '-',
          status: q.status,
          completed_at: q.completed_at,
          current_streak: q.response_data?.current_streak || 0,
          daily_completions: q.response_data?.daily_completions || []
        }));

        const content = template.content as any || {};
        
        challengeData.push({
          id: template.id,
          title: template.title,
          description: template.description || '',
          target_type: template.intervention_type === 'cluster_challenge' ? 'cluster' : 'individual',
          created_at: template.created_at,
          expires_at: null,
          content: {
            challenge_type: content.challenge_type || 'daily_checkin',
            duration_days: content.duration_days || 7,
            target_days: content.target_days || 7
          },
          participants
        });
      }

      setChallenges(challengeData);
    } catch (error) {
      console.error('Error loading challenges:', error);
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  const getCompletionRate = (participants: ChallengeParticipant[]) => {
    if (participants.length === 0) return 0;
    const completed = participants.filter(p => p.status === 'completed').length;
    return Math.round((completed / participants.length) * 100);
  };

  const getAverageStreak = (participants: ChallengeParticipant[]) => {
    if (participants.length === 0) return 0;
    const total = participants.reduce((acc, p) => acc + p.current_streak, 0);
    return Math.round(total / participants.length);
  };

  const getTopPerformers = (participants: ChallengeParticipant[]) => {
    return [...participants]
      .sort((a, b) => b.current_streak - a.current_streak)
      .slice(0, 5);
  };

  // Stats across all challenges
  const totalParticipants = challenges.reduce((acc, c) => acc + c.participants.length, 0);
  const avgCompletionRate = challenges.length > 0 
    ? Math.round(challenges.reduce((acc, c) => acc + getCompletionRate(c.participants), 0) / challenges.length)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Target className="h-5 w-5 text-purple-500" />
            <div>
              <p className="text-2xl font-bold">{challenges.length}</p>
              <p className="text-sm text-muted-foreground">Active Challenges</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{totalParticipants}</p>
              <p className="text-sm text-muted-foreground">Participants</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{avgCompletionRate}%</p>
              <p className="text-sm text-muted-foreground">Avg Completion</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Flame className="h-5 w-5 text-orange-500" />
            <div>
              <p className="text-2xl font-bold">
                {challenges.length > 0 
                  ? Math.round(challenges.reduce((acc, c) => acc + getAverageStreak(c.participants), 0) / challenges.length)
                  : 0
                }
              </p>
              <p className="text-sm text-muted-foreground">Avg Streak</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Challenge Cards */}
      <div className="space-y-4">
        {challenges.length === 0 ? (
          <Card className="p-8 text-center">
            <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No challenges created yet</p>
          </Card>
        ) : (
          challenges.map((challenge, index) => {
            const completionRate = getCompletionRate(challenge.participants);
            const avgStreak = getAverageStreak(challenge.participants);
            const topPerformers = getTopPerformers(challenge.participants);

            return (
              <motion.div
                key={challenge.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{challenge.title}</h3>
                        <Badge variant={challenge.target_type === 'cluster' ? 'default' : 'outline'}>
                          {challenge.target_type === 'cluster' ? 'Team' : 'Individual'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{challenge.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {challenge.content.duration_days} days
                      </div>
                    </div>
                  </div>

                  {/* Progress Overview */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-2 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold">{challenge.participants.length}</p>
                      <p className="text-xs text-muted-foreground">Participants</p>
                    </div>
                    <div className="text-center p-2 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold text-green-500">{completionRate}%</p>
                      <p className="text-xs text-muted-foreground">Completed</p>
                    </div>
                    <div className="text-center p-2 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-500">{avgStreak}</p>
                      <p className="text-xs text-muted-foreground">Avg Streak</p>
                    </div>
                  </div>

                  {/* Top Performers */}
                  {topPerformers.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-amber-500" />
                        Top Performers
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {topPerformers.map((performer, idx) => (
                          <Badge 
                            key={performer.worker_id} 
                            variant="outline"
                            className={idx === 0 ? 'border-amber-500 text-amber-600' : ''}
                          >
                            {idx === 0 && '🏆 '}
                            {performer.worker_name}
                            <span className="ml-1 text-orange-500">
                              <Flame className="h-3 w-3 inline" />
                              {performer.current_streak}
                            </span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Calendar View for recent activity */}
                  <div className="mt-4">
                    <h4 className="text-sm font-medium mb-2">Last 7 Days Activity</h4>
                    <div className="flex gap-1">
                      {Array.from({ length: 7 }).map((_, dayIdx) => {
                        const date = new Date();
                        date.setDate(date.getDate() - (6 - dayIdx));
                        const dateStr = date.toISOString().split('T')[0];
                        
                        const activeCount = challenge.participants.filter(p => 
                          p.daily_completions.includes(dateStr)
                        ).length;
                        
                        const intensity = challenge.participants.length > 0 
                          ? activeCount / challenge.participants.length 
                          : 0;

                        return (
                          <div key={dayIdx} className="flex-1 text-center">
                            <div 
                              className="h-8 rounded-md flex items-center justify-center text-xs font-medium"
                              style={{
                                backgroundColor: `hsl(var(--primary) / ${0.1 + intensity * 0.8})`,
                                color: intensity > 0.5 ? 'white' : 'inherit'
                              }}
                            >
                              {activeCount}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {date.toLocaleDateString('id-ID', { weekday: 'short' })}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}
